import type {
  GenericPlatformMethod,
  MethodToFirstArgMap,
  Platform,
} from './platform';
import { Odoo } from './odoo';
import { z } from 'zod';

const DirectoryBookingSchema = z
  .object({
    customerName: z.string(),
    customerEmail: z.email(),
    organisationName: z.string(),
    departmentName: z.string(),
    phoneNumber: z.string(),
    appointmentDate: z.string(),
    appointmentTime: z.string(),
    description: z.string(),
    requestedDeafUserName: z.string(),
  })
  .required();

export type DirectoryBooking = z.infer<typeof DirectoryBookingSchema>;

export type Response = SuccessResponse | FailureResponse;

export type SuccessResponse = {
  success: true;
  error: undefined;
};

export type FailureResponse = {
  success: false;
  error: CRMError;
};

export type CRMResponse = {
  platform: Platform['platform'];
} & Response;

export class CRMError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CRMError';
  }
}

export class NotImplementedError extends CRMError {
  constructor(methodName: string, platform: string) {
    super(`Method ${methodName} not implemented for platform ${platform}`);
    this.name = 'NotImplementedError';
  }
}

class CustomerRelationshipManagement {
  platforms: Platform[];

  constructor(platforms: Platform[]) {
    this.platforms = platforms;
  }

  // Unwraps the settled promises into CRMResponse format
  private unwrapResponses(
    responses: PromiseSettledResult<Response>[]
  ): CRMResponse[] {
    const response = responses.map((response, index) => {
      if (response.status === 'fulfilled') {
        return { platform: this.platforms[index].platform, ...response.value };
      }

      return {
        platform: this.platforms[index].platform,
        success: false,
        error: response.reason.message,
      };
    });

    return response;
  }

  // Calls the given method on all platforms, passing the argument if required
  // Some type gymnastics to infer if the method needs an argument or not
  private async call<T extends GenericPlatformMethod>(
    fnName: T,
    ...args: MethodToFirstArgMap[T] extends never
      ? []
      : [MethodToFirstArgMap[T]]
  ): Promise<CRMResponse[]> {
    const arg = args[0] as MethodToFirstArgMap[T] | never;

    const fns = this.platforms.map((platform) => {
      const maybe = platform[fnName] as unknown;
      if (typeof maybe === 'function') {
        const fn = maybe;
        return () => fn.call(platform, arg);
      }

      return () =>
        Promise.resolve({
          success: false,
          error: new CRMError(`Method ${String(fnName)} does not exist`),
        } as FailureResponse);
    });

    const responses = await Promise.allSettled(fns.map((fn) => fn()));

    return this.unwrapResponses(responses);
  }

  async recordDirectoryBooking(
    details: DirectoryBooking
  ): Promise<CRMResponse[]> {
    const result = DirectoryBookingSchema.safeParse(details);

    if (!result.success) {
      throw result.error;
    }

    return await this.call('recordDirectoryBooking', result.data);
  }
}

const CRM = new CustomerRelationshipManagement([new Odoo()]);

export default CRM;
